import { create } from 'zustand'
import { fetchSystemHealth } from '@/features/health/api'
import type { SystemHealthResponse } from '@/features/health/types'

export type ServiceStatus =
  | 'healthy'
  | 'unhealthy'
  | 'offline'
  | 'not_deployed'
  | 'degraded'
  | 'checking'
  | 'unknown'

export interface ServiceState {
  name: string
  displayName: string
  status: ServiceStatus
  latencyMs?: number
  error?: string
  lastChecked: number | null
}

interface ConnectionState {
  // Event stream connection (separate from service health)
  eventStreamConnected: boolean
  eventStreamLoading: boolean

  // All backend services
  services: ServiceState[]
  isCheckingHealth: boolean
  lastHealthCheck: number | null
  healthCheckError: string | null

  // Summary
  summary: {
    total: number
    healthy: number
    unhealthy: number
  }
}

interface ConnectionActions {
  setEventStreamStatus: (connected: boolean, loading?: boolean) => void
  checkSystemHealth: () => Promise<void>
  getServiceByName: (name: string) => ServiceState | undefined
}

// Map service names to display names
// const SERVICE_DISPLAY_NAMES: Record<string, string> = {
//   identity: 'Identity',
//   catalog: 'Catalog',
//   payments: 'Payments',
//   operations: 'Operations',
//   communications: 'Communications',
//   events: 'Events',
//   agents: 'Agents',
//   support: 'Support',
//   admin: 'Admin',
//   marketing: 'Marketing',
//   engineering: 'Engineering',
//   experiments: 'Experiments',
//   sdkconfig: 'SDK Config',
//   'client-engagement': 'Client Engagement',
//   'webhook-ingester': 'Webhook Ingester',
//   'webhook-router': 'Webhook Router',
//   mcp: 'MCP',
//   'mac-worker': 'Mac Worker',
// }

// Map API status strings to our status type
// function mapApiStatus(status: string): ServiceStatus {
//   switch (status.toLowerCase()) {
//     case 'healthy':
//       return 'healthy'
//     case 'unhealthy':
//       return 'unhealthy'
//     case 'offline':
//       return 'offline'
//     case 'not_deployed':
//       return 'not_deployed'
//     case 'degraded':
//       return 'degraded'
//     default:
//       return 'unknown'
//   }
// }

// function getDisplayName(name: string): string {
//   return (
//     SERVICE_DISPLAY_NAMES[name.toLowerCase()] ||
//     name
//       .split('-')
//       .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
//       .join(' ')
//   )
// }

function mapApiStatusToServiceStatus(status: string): ServiceStatus {
  switch (status.toLowerCase()) {
    case 'healthy':
      return 'healthy'
    case 'unhealthy':
      return 'unhealthy'
    case 'offline':
      return 'offline'
    case 'not_deployed':
      return 'not_deployed'
    case 'degraded':
      return 'degraded'
    case 'checking':
      return 'checking'
    default:
      return 'unknown'
  }
}

export const useConnectionStore = create<ConnectionState & ConnectionActions>(
  (set, get) => ({
    // Initial state
    eventStreamConnected: false,
    eventStreamLoading: false,
    services: [],
    isCheckingHealth: false,
    lastHealthCheck: null,
    healthCheckError: null,
    summary: {
      total: 0,
      healthy: 0,
      unhealthy: 0,
    },

    setEventStreamStatus: (connected, loading = false) => {
      set({ eventStreamConnected: connected, eventStreamLoading: loading })
    },

    checkSystemHealth: async () => {
      const { isCheckingHealth } = get()
      if (isCheckingHealth) return

      set({ isCheckingHealth: true, healthCheckError: null })

      try {
        const response: SystemHealthResponse = await fetchSystemHealth()

        // Map API response to connection store format
        const services: ServiceState[] = response.services.map((service) => ({
          name: service.name.toLowerCase(),
          displayName: service.name
            .split('-')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
            .join(' '),
          status: mapApiStatusToServiceStatus(service.status),
          latencyMs: service.latency_ms,
          error: service.error,
          lastChecked: new Date(service.checked_at).getTime(),
        }))

        const healthyCount = services.filter(
          (s) => s.status === 'healthy',
        ).length
        const unhealthyCount = services.length - healthyCount

        set({
          services,
          summary: {
            total: services.length,
            healthy: healthyCount,
            unhealthy: unhealthyCount,
          },
          lastHealthCheck: Date.now(),
          isCheckingHealth: false,
          healthCheckError: null,
        })
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : typeof err === 'string'
              ? err
              : 'Failed to check health'
        console.error('[Health] Error:', errorMessage)
        set({
          isCheckingHealth: false,
          healthCheckError: errorMessage,
        })
      }
    },

    getServiceByName: (name) => {
      return get().services.find((s) => s.name.toLowerCase() === name.toLowerCase())
    },
  })
)
